import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { get } from "@/api";
import {
  getDiscountCustomColor,
  getDiscountText,
  getListingsWithDiscountInformation,
  hasLinearPricing,
} from "@/services/ListingHelperService";
import type { ListingTypeOptions } from "@/utils/productTypes";
import type { Models } from "api/types";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { AdvisorCTA } from "modules/adonis/AdvisorCTA/AdvisorCTA";
import { AdvisorShopCategoryHeader } from "modules/adonis/AdvisorShopCategoryHeader/AdvisorShopCategoryHeader";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { useFeatureFlag } from "services/Flags";
import { notNull } from "toolbox/Guards";

const ShopRecommendedTreatmentsPage = () => {
  const navigate = useNavigate();
  const { openChat } = useAdonisChat();
  const adonisShopCategoryHeaderText = useFeatureFlag(
    "adonisShopCategoryHeaderText"
  );
  const adonisV3CTAText = useFeatureFlag("adonisV3CTAText");

  const fetchTopRecommendations = async (): Promise<
    Models<"AdonisTopRecommendationModel">[]
  > => {
    return get("/adonis/top-recommendations", {});
  };

  const { data: recommendations = [], isLoading } = useQuery<
    Models<"AdonisTopRecommendationModel">[]
  >({
    queryKey: ["/adonis/top-recommendations", {}],
    queryFn: fetchTopRecommendations,
    staleTime: 1000 * 30,
  });

  const { data: shopData } = useQuery({
    queryKey: ["/shop"],
    queryFn: () => get("/shop"),
  });
  const { data: shopDiscounts } = useQuery({
    queryKey: ["/shop/discounts"],
    queryFn: () => get("/shop/discounts"),
    staleTime: 1000 * 10,
  });
  const { data: customShopCategories } = useQuery({
    queryKey: ["/shop/custom-categories/listings"],
    queryFn: () => get("/shop/custom-categories/listings"),
    staleTime: 1000 * 10,
  });
  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });
  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
    staleTime: 1000 * 60 * 2,
  });

  const listingsWithDiscounts = getListingsWithDiscountInformation(
    recommendations,
    customShopCategories,
    shopDiscounts?.listingDiscounts
  ).map((listing) => {
    if (listing.type === "Membership") {
      const membershipDiscount =
        shopDiscounts?.membershipDiscounts?.[listing.id];
      return {
        ...listing,
        discountText: getDiscountText(membershipDiscount),
        discount: membershipDiscount,
        customColor: getDiscountCustomColor(
          membershipDiscount,
          customShopCategories
        ),
      };
    }
    return listing;
  });

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const handleAdonisCTAClick = () => {
    openChat();
    trackAdonisViewed();
  };

  return (
    <div className="flex flex-col bg-two">
      <AdvisorShopCategoryHeader headerText={adonisShopCategoryHeaderText} />

      <div className="flex flex-col gap-3 p-6">
        {!isLoading &&
          listingsWithDiscounts.map(
            (
              {
                id,
                type,
                title,
                description,
                imageUrl,
                concernIds,
                baseCost,
                membershipCost,
                packageUnitType,
                packages,
                discountText,
                customColor,
              },
              index
            ) => {
              const handleClick = () => {
                const isProduct =
                  type === "Product" || type === "ProductFromExternalSource";
                const isMembership = type === "Membership";

                navigate(
                  isProduct
                    ? `/pdp/product/${id}`
                    : isMembership
                    ? `/pdp/membership/${id}`
                    : `/pdp/package/${id}`
                );
              };

              return (
                <Fragment key={id}>
                  <ProductCardLarge
                    isRecommended
                    key={id}
                    itemId={id}
                    title={title ?? ""}
                    description={description}
                    imageUrl={imageUrl ?? ""}
                    type={type as ListingTypeOptions}
                    tag=""
                    concerns={shopData?.concerns
                      ?.filter(({ id }) => (concernIds ?? []).includes(id))
                      .map(({ name }) => name)
                      .filter(notNull)}
                    price={baseCost ?? 0}
                    memberPrice={membershipCost}
                    onClick={handleClick}
                    unitType={packageUnitType?.singularUnitType}
                    hasLinearPricing={(() => {
                      const packagePrices = (packages ?? []).flatMap(
                        (p) => p.packagePrices
                      ) as Models<"PackagePriceModel">[];
                      return hasLinearPricing(packagePrices);
                    })()}
                    discount={discountText}
                    customColor={customColor}
                    guestMode={user?.isGuest}
                    bookingEnabled={
                      platform?.platformSettings?.partnerEmrBookingEnabled
                    }
                  />
                  {(listingsWithDiscounts.length === 1 || index === 1) && (
                    <AdvisorCTA
                      ctaText={adonisV3CTAText}
                      onChatClick={handleAdonisCTAClick}
                      className="rounded-lg shadow-subtle"
                    />
                  )}
                </Fragment>
              );
            }
          )}
      </div>

      <RepeatMDFooter />
    </div>
  );
};

export default ShopRecommendedTreatmentsPage;
